import { z } from 'zod';

import {
  VERIFICATION_DECISION_REASONS,
  type VerificationDecisionReason,
} from '../verification-decision/verification-decision';

/**
 * What a case is *for* (D6).
 *
 * - **`initial`** — the first look: evidence steps materialized per gender, an
 *   interview, the face match; the member is `pending_verification` until it is
 *   decided, and a refusal makes them `rejected`.
 * - **`re_review`** — an approved member edited an answer staff marked
 *   `requiresReviewOnEdit`. No steps: what is reviewed is the change itself
 *   (`answerChanges`). The member is gated while it is open; **approve** returns
 *   them to `approved` with the new answers, **reject** restores the previous
 *   answers and returns them to `approved` — a refused edit is not a refused
 *   member.
 */
export const VERIFICATION_CASE_KINDS = ['initial', 're_review'] as const;

export const VerificationCaseKindSchema = z.enum(VERIFICATION_CASE_KINDS);

export type VerificationCaseKind = (typeof VERIFICATION_CASE_KINDS)[number];

/**
 * A re-review collects no evidence, so no reason about evidence can be true of
 * it; what the reviewer judged was the edited answer.
 */
const RE_REVIEW_REASONS: readonly VerificationDecisionReason[] = [
  'identity_mismatch',
  'suspected_fraud',
  'policy_violation',
  'other',
];

/** The reasons a reviewer may refuse a case of this kind with — what the reject modal offers. */
export function decisionReasonsForKind(
  kind: VerificationCaseKind,
): readonly VerificationDecisionReason[] {
  return kind === 're_review' ? RE_REVIEW_REASONS : VERIFICATION_DECISION_REASONS;
}

export function isDecisionReasonForKind(
  kind: VerificationCaseKind,
  reason: VerificationDecisionReason,
): boolean {
  return decisionReasonsForKind(kind).includes(reason);
}
